import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../stores/useAuthStore';
import { legalService } from '../../../services/legalService';
import { PRIVACY_CONTACT_EMAIL } from '../../../constants/legal';
import { Card } from '../../ui';

/**
 * Perfil › Segurança: privacidade do titular (LGPD). Mostra a versão dos
 * Termos/Política aceita, liga e desliga a autorização de uso das imagens em
 * portfólio/marketing (revogar é tão fácil quanto autorizar) e indica o canal
 * para acesso, correção, portabilidade e exclusão dos dados.
 */
const PrivacySettingsCard: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const user = useAuthStore((s) => s.user);
  const [saving, setSaving] = useState(false);
  const status = user?.legalTerms;
  if (!user || !status) return null;

  const toggle = async () => {
    setSaving(true);
    try {
      const next = await legalService.setMarketingImagesConsent(!status.marketingImagesConsent);
      useAuthStore.setState({ user: { ...user, legalTerms: next } });
      toast.success(next.marketingImagesConsent ? t('legal.profile.granted') : t('legal.profile.revoked'));
    } catch {
      toast.error(t('legal.acceptFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 max-w-md mt-6">
      <h3 className="text-lg font-semibold text-snapfy-text-primary mb-1">{t('legal.profile.title')}</h3>
      <p className="text-sm text-snapfy-text-secondary mb-4">
        {status.acceptedAt
          ? t('legal.profile.accepted', { version: status.acceptedVersion, date: new Date(status.acceptedAt).toLocaleDateString(i18n.language) })
          : t('legal.profile.notAccepted')}{' '}
        <a href="/terms" target="_blank" rel="noopener" className="text-snapfy-accent-soft underline underline-offset-2">
          {t('legal.terms')}
        </a>{' '}
        ·{' '}
        <a href="/privacy" target="_blank" rel="noopener" className="text-snapfy-accent-soft underline underline-offset-2">
          {t('legal.privacy')}
        </a>
      </p>
      <label className="flex items-start gap-3 text-sm text-snapfy-text-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={status.marketingImagesConsent}
          onChange={() => void toggle()}
          disabled={saving}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-snapfy-border accent-[#7063f7]"
        />
        <span>{t('legal.marketingImages')}</span>
      </label>
      <p className="text-xs text-snapfy-text-secondary mt-4">
        {t('legal.profile.rights')}{' '}
        <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-snapfy-accent-soft underline underline-offset-2">
          {PRIVACY_CONTACT_EMAIL}
        </a>
        .
      </p>
    </Card>
  );
};

export default PrivacySettingsCard;
